import JSZip from 'jszip';
import { OOXML_PARTS } from './constants';
import type { DocxXmlParts } from './types';

// Firefox/jsdom wrap XML parse failures in a <parsererror> under this
// namespace; Chromium emits an XHTML <parsererror> instead — check both.
const FIREFOX_PARSER_ERROR_NS = 'http://www.mozilla.org/newlayout/xml/parsererror.xml';

function hasParserError(doc: Document): boolean {
    if (doc.getElementsByTagNameNS(FIREFOX_PARSER_ERROR_NS, 'parsererror').length > 0) return true;
    if (doc.documentElement?.localName === 'parsererror') return true;
    return doc.getElementsByTagName('parsererror').length > 0;
}

export function parseXml(xml: string | null): Document | null {
    if (!xml) return null;
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    return hasParserError(doc) ? null : doc;
}

// Word and Google Docs both use the `w:` prefix, but prefix strings are not
// guaranteed by the XML spec — all lookups below match on localName so any
// prefix (and the strict-vs-transitional OOXML namespaces) resolve the same.
export function elementChildren(el: Element | null): Element[] {
    if (!el) return [];
    return Array.from(el.children);
}

export function childByLocalName(el: Element | null, localName: string): Element | null {
    if (!el) return null;
    for (const child of Array.from(el.children)) {
        if (child.localName === localName) return child;
    }
    return null;
}

export function childrenByLocalName(el: Element | null, localName: string): Element[] {
    return elementChildren(el).filter((child) => child.localName === localName);
}

export function attrByLocalName(el: Element | null, localName: string): string | null {
    if (!el) return null;
    for (const attr of Array.from(el.attributes)) {
        if (attr.localName === localName) return attr.value;
    }
    return null;
}

/** Shorthand for the ubiquitous `w:val` attribute. */
export function val(el: Element | null): string | null {
    return attrByLocalName(el, 'val');
}

export function intAttr(el: Element | null, localName: string): number | undefined {
    const raw = attrByLocalName(el, localName);
    if (raw == null) return undefined;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
}

export async function readDocxXml(arrayBuffer: ArrayBuffer): Promise<DocxXmlParts> {
    const zip = await JSZip.loadAsync(arrayBuffer);

    const readPart = async (path: string): Promise<string | null> => {
        const file = zip.file(path);
        if (!file) return null;
        return file.async('string');
    };

    const [documentXml, stylesXml, numberingXml, themeXml] = await Promise.all([
        readPart(OOXML_PARTS.DOCUMENT),
        readPart(OOXML_PARTS.STYLES),
        readPart(OOXML_PARTS.NUMBERING),
        readPart(OOXML_PARTS.THEME),
    ]);

    const document = parseXml(documentXml);
    if (!document) {
        throw new Error(`Not a readable .docx: ${OOXML_PARTS.DOCUMENT} is missing or invalid`);
    }

    return {
        document,
        styles: parseXml(stylesXml),
        numbering: parseXml(numberingXml),
        theme: parseXml(themeXml),
    };
}
